import { forwardRef } from "react";
import Constellation from "./Constellation";
import { HERO_VIDEO_SRC, logoFor } from "./content";
import type { ThemeVals } from "./theme";

/**
 * The canvas's `Hero` screen.
 *
 * Four stacked backdrops sit behind the copy, and only two are ever visible at
 * once. Light mode shows the video plus a paper-coloured gradient that fades it
 * into the section below; dark mode cross-fades to a starfield, two rings and a
 * cyan bloom, and reveals the perspective floor grid. The theme values arrive
 * pre-resolved so this component never branches on the theme itself.
 *
 * The video wrapper carries `data-plx-direct`, which opts it out of the rAF
 * lerp. Everything else eases toward its target; a video layer that eases
 * visibly lags the frame it is composited into.
 */

const SIDEBAR = [
  "CRM",
  "HRM",
  "Projects",
  "Finance",
  "Marketing",
  "AI Agents",
  "Reports",
];

const STATS: Array<{ label: string; value: string; delta: string }> = [
  { label: "Total Revenue", value: "$245,720", delta: "+12.5%" },
  { label: "New Leads", value: "1,540", delta: "+18.7%" },
  { label: "Active Projects", value: "24", delta: "+5" },
];

const BAR_HEIGHTS = [38, 52, 44, 66, 58, 74, 64, 88];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"];

const monthStyle: React.CSSProperties = {
  flex: 1,
  textAlign: "center",
  fontFamily: "'Manrope', sans-serif",
  fontSize: 9.5,
  color: "rgba(var(--ink-rgb),.62)",
};

const dotStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: "rgba(var(--ink-rgb),.16)",
};

const navItemStyle: React.CSSProperties = {
  padding: "7px 10px",
  fontSize: 12.5,
  color: "rgba(var(--ink-rgb),.62)",
};

interface HeroProps {
  vals: ThemeVals;
  videoRef: React.RefObject<HTMLVideoElement>;
  dark: boolean;
}

const Hero = forwardRef<HTMLElement, HeroProps>(function Hero(
  { vals, videoRef, dark },
  ref,
) {
  return (
    <header
      id="hero"
      ref={ref}
      className="site-gutter"
      style={{
        position: "relative",
        overflow: "clip",
        paddingTop: 96,
        minHeight: "92vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Light-mode backdrop: video, then a gradient that lands it on paper. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          opacity: vals.heroVideoOpacity,
          visibility: vals.heroVideoVis,
          transition: "opacity .45s",
        }}
      >
        <div
          data-plx="0.12"
          data-plx-max="80"
          data-plx-direct=""
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: -90,
            bottom: -90,
            transform: "translate3d(0,0,0)",
            willChange: "transform",
          }}
        >
          <video
            ref={videoRef}
            data-hero-video=""
            src={HERO_VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              backfaceVisibility: "hidden",
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, color-mix(in srgb, var(--paper) 62%, transparent), color-mix(in srgb, var(--paper) 74%, transparent) 55%, var(--paper) 98%)",
          }}
        />
      </div>

      {/* Cyan bloom. Present in both themes. Keeps its own placement, because
          the hero is a composed scene rather than generic atmosphere, but takes
          the shared eased falloff so it decays like every other light on the
          page instead of on a straight ramp. */}
      <div
        className="dc-bloom"
        data-plx="0.15"
        style={{
          top: -220,
          left: "8%",
          width: 760,
          height: 560,
          borderRadius: 0,
          filter: "blur(8px)",
          ["--dc-bloom-a" as string]: "0.24",
        }}
      />

      {/* Dark-mode backdrop: two starfields at different rates, a bloom, two rings. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: vals.darkFxOpacity,
          visibility: vals.darkFxVis,
          transition: "opacity .45s",
          pointerEvents: "none",
          /* Bottom-only fade. The bloom inside sits at 60% with a 560px box, so
             it overhangs the hero by roughly 220px and used to be sheared off
             flat by overflow:clip, laying a lit edge along the seam with
             Capabilities. Faded here instead, so the light has left before the
             boundary arrives. Bottom only: the starfield should still reach the
             nav, and there is no section above to seam against. */
          WebkitMaskImage:
            "linear-gradient(to bottom, #000 0, #000 calc(100% - 230px), transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, #000 0, #000 calc(100% - 230px), transparent 100%)",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
        }}
      >
        <div
          data-plx="0.05"
          style={{
            position: "absolute",
            inset: "-12%",
            backgroundImage:
              "radial-gradient(rgba(255,255,255,.2) 1px, transparent 1.4px)",
            backgroundSize: "96px 96px",
            transform: "translate3d(0,0,0)",
          }}
        />
        <div
          data-plx="0.18"
          style={{
            position: "absolute",
            inset: "-12%",
            backgroundImage:
              "radial-gradient(rgba(56,229,255,.4) 1.2px, transparent 1.8px)",
            backgroundSize: "150px 150px",
            backgroundPosition: "44px 66px",
            opacity: 0.55,
            transform: "translate3d(0,0,0)",
          }}
        />
        <div
          className="dc-bloom"
          data-plx="-0.1"
          style={{
            top: "60%",
            right: -180,
            width: 560,
            height: 560,
            ["--dc-bloom-a" as string]: "0.12",
          }}
        />
        <div
          data-plx="0.08"
          style={{
            position: "absolute",
            top: -140,
            right: "6%",
            width: 420,
            height: 420,
            border: "1px solid rgba(56,229,255,.14)",
            borderRadius: "50%",
          }}
        />
        <div
          data-plx="-0.06"
          style={{
            position: "absolute",
            top: -190,
            right: "1%",
            width: 560,
            height: 560,
            border: "1px dashed rgba(255,255,255,.08)",
            borderRadius: "50%",
          }}
        />
      </div>

      {/* Perspective floor grid. Dark mode only, masked so it fades in from nothing. */}
      <div
        style={{
          position: "absolute",
          left: "-10%",
          right: "-10%",
          bottom: -60,
          height: "46%",
          backgroundImage:
            "linear-gradient(rgba(var(--ink-rgb),.22) 1px, transparent 1px), linear-gradient(90deg, rgba(var(--ink-rgb),.22) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          transform: "perspective(620px) rotateX(62deg)",
          transformOrigin: "top center",
          animation: "dcv2-gridFlow 2.6s linear infinite",
          opacity: vals.heroGridOpacity,
          transition: "opacity .45s",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 2%, black 46%)",
          maskImage: "linear-gradient(to bottom, transparent 2%, black 46%)",
          pointerEvents: "none",
        }}
      />

      {/* Above every backdrop, below the copy at zIndex 3. */}
      <Constellation dark={dark} />

      <div
        style={{
          flex: 1,
          width: "100%",
          maxWidth: 1240,
          margin: "0 auto",
          position: "relative",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            margin: "auto 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth: 880,
          }}
        >
          {/* The "AI Operating System for Modern Businesses" pill that used to
              sit here is gone. It was a kicker doing the headline's job in a
              quieter voice, and the headline does not need the help — the
              first thing in the hero is now the claim itself. Removing it also
              takes the top margin with it, so the h1 opens the block. */}
          <h1
            style={{
              margin: 0,
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(38px, 4.6vw, 64px)",
              lineHeight: 1.08,
              letterSpacing: "-0.025em",
            }}
          >
            {/* One sentence per line, broken explicitly rather than left to
                wrap. The canvas set the first two as a single inline run, so
                where they split depended on the viewport — at 1366 it broke
                after "Command" and stranded "Smarter." on its own. Three
                blocks give the same three lines at every width. */}
            <span style={{ display: "block" }}>Aim Higher.</span>
            <span style={{ display: "block" }}>Command Smarter.</span>
            <span style={{ display: "block", color: "var(--acc2)" }}>
              Execute Better.
            </span>
          </h1>
          <p
            style={{
              margin: "24px 0 0",
              maxWidth: 560,
              fontSize: 17,
              color: "rgba(var(--ink-rgb),.7)",
            }}
          >
            AI2Aim unifies your people, workflows, and AI agents in one
            intelligent platform to drive growth, efficiency, and measurable
            results
          </p>
          <div
            style={{
              marginTop: 34,
              display: "flex",
              justifyContent: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <a
              href="#capabilities"
              className="dc-h-btn-lg"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: "var(--btn)",
                color: "var(--btnfg)",
                padding: "15px 28px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                transition: "transform .2s, box-shadow .2s",
              }}
            >
              Explore AI2Aim <span aria-hidden="true">&rarr;</span>
            </a>
            <a
              href="#how"
              className="dc-h-ghost"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                border: "1px solid rgba(var(--ink-rgb),.3)",
                color: "var(--ink)",
                padding: "15px 28px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                transition: "border-color .2s, background .2s",
              }}
            >
              How It Works{" "}
              <span aria-hidden="true" style={{ fontSize: 11 }}>
                &#9654;
              </span>
            </a>
          </div>
          <div
            style={{
              marginTop: 44,
              fontFamily: "'Manrope', sans-serif",
              fontSize: 11,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "rgba(var(--ink-rgb),.62)",
            }}
          >
            AI Agents &middot; Workflows &middot; Analytics &middot; CRM &middot;
            HRM &middot; Finance
          </div>
        </div>

        {/* Dashboard mock, cropped by the section's bottom edge. */}
        <div
          data-plx="0.05"
          data-plx-max="30"
          style={{
            width: "min(1000px, 98%)",
            margin: "52px 0 0",
            position: "relative",
          }}
        >
          <div
            style={{
              background: "var(--paper2)",
              border: "1px solid rgba(var(--ink-rgb),.16)",
              borderBottom: "none",
              borderRadius: "20px 20px 0 0",
              boxShadow: "0 30px 80px rgba(9,9,7,.18)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "13px 18px",
                borderBottom: "1px solid rgba(var(--ink-rgb),.1)",
              }}
            >
              <span style={dotStyle} />
              <span style={dotStyle} />
              <span style={dotStyle} />
              <span
                style={{
                  marginLeft: 8,
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 11,
                  color: "rgba(var(--ink-rgb),.62)",
                }}
              >
                AI2Aim Dashboard
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 10,
                  color: "var(--pos)",
                }}
              >
                &#9679; Live
              </span>
            </div>
            {/* Sidebar beside the stats from tablet up; on a phone the sidebar
                goes and the stats wrap (global.css, T101026002). */}
            <div className="home-mock-body">
              <div
                className="home-mock-side"
                style={{
                  borderRight: "1px solid rgba(var(--ink-rgb),.09)",
                  padding: "14px 12px",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 10px 13px",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {/* `dark`, not `true`. Unlike the DashboardDark render — which
                      is painted on literal #15181D and is dark in both themes —
                      this panel is built from `var(--ink-rgb)` and follows the
                      page, so on light mode it is a pale surface. Passing `true`
                      here put the white master on near-white and the mark
                      vanished. */}
                  <img
                    src={logoFor(dark)}
                    alt=""
                    aria-hidden="true"
                    style={{ width: 18, height: 18, display: "block" }}
                  />{" "}
                  AI2Aim
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 10px",
                    borderRadius: 8,
                    background: "rgba(56,229,255,.12)",
                    color: "var(--acc2)",
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      width: 3,
                      height: 13,
                      borderRadius: 2,
                      background: "var(--acc2)",
                    }}
                  />
                  Dashboard
                </div>
                {SIDEBAR.map((item) => (
                  <div key={item} style={navItemStyle}>
                    {item}
                  </div>
                ))}
              </div>
              <div className="home-mock-main">
                <div className="home-mock-stats">
                  {STATS.map((s) => (
                    <div
                      key={s.label}
                      style={{
                        border: "1px solid rgba(var(--ink-rgb),.12)",
                        borderRadius: 12,
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "rgba(var(--ink-rgb),.62)",
                        }}
                      >
                        {s.label}
                      </div>
                      <div
                        style={{
                          fontSize: 19,
                          fontWeight: 800,
                          margin: "3px 0",
                        }}
                      >
                        {s.value}
                      </div>
                      <div style={{ fontSize: 10.5 }}>
                        <span style={{ color: "var(--pos)", fontWeight: 700 }}>
                          {s.delta}
                        </span>{" "}
                        <span style={{ color: "rgba(var(--ink-rgb),.62)" }}>
                          vs last month
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    border: "1px solid rgba(var(--ink-rgb),.12)",
                    borderRadius: 12,
                    padding: "14px 16px",
                    marginTop: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                      Revenue Overview
                    </span>
                    <span
                      style={{
                        fontSize: 10.5,
                        color: "var(--acc2)",
                        border: "1px solid rgba(56,229,255,.4)",
                        borderRadius: 999,
                        padding: "3px 10px",
                      }}
                    >
                      This Year
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 12,
                      height: 88,
                    }}
                  >
                    {BAR_HEIGHTS.map((h, i) => (
                      <span
                        key={i}
                        style={{
                          flex: 1,
                          height: h,
                          borderRadius: "5px 5px 2px 2px",
                          background: "linear-gradient(180deg, #38E5FF, #0E6E80)",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 12, paddingTop: 8 }}>
                    {MONTHS.map((m) => (
                      <span key={m} style={monthStyle}>
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
});

export default Hero;
